import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Gamepad2 } from 'lucide-react';
import Layout from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import { config } from '@/config';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import SeasonRankingPanel, { RANKING_VIEW_OPTIONS } from '@/components/games/SeasonRankingPanel';
import { FilterPills } from '@/components/games/GameResultsPanel';

const API = config.BACKEND_URL;

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const MEDAL_STYLES = {
  gold: {
    label: 'Or',
    text: 'text-amber-600 dark:text-amber-300',
    fill: ['#F8E7A0', '#E0B94A', '#B8860B'],
    stroke: '#8A6A12',
  },
  silver: {
    label: 'Argent',
    text: 'text-slate-600 dark:text-slate-300',
    fill: ['#F4F4F5', '#C5C9CE', '#8E949B'],
    stroke: '#6B7178',
  },
  bronze: {
    label: 'Bronze',
    text: 'text-orange-800 dark:text-orange-300',
    fill: ['#E8B07A', '#CD7F32', '#8B4513'],
    stroke: '#6A3410',
  },
};

function TrophyCup({ medal, size = 48, uid = 'x' }) {
  const style = MEDAL_STYLES[medal] || MEDAL_STYLES.bronze;
  const [hi, mid, lo] = style.fill;
  const gid = `cup-${medal}-${uid}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      aria-hidden="true"
      className="shrink-0 drop-shadow-md"
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={hi} />
          <stop offset="48%" stopColor={mid} />
          <stop offset="100%" stopColor={lo} />
        </linearGradient>
      </defs>
      <path
        d="M14 16 C2 18 2 34 16 36"
        fill="none"
        stroke={`url(#${gid})`}
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M50 16 C62 18 62 34 48 36"
        fill="none"
        stroke={`url(#${gid})`}
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M18 12 h28 a4 4 0 0 1 4 4 v6 c0 12 -8 20 -18 20 s-18 -8 -18 -20 v-6 a4 4 0 0 1 4 -4 z"
        fill={`url(#${gid})`}
        stroke={style.stroke}
        strokeWidth="1.2"
      />
      <ellipse cx="32" cy="13.5" rx="13" ry="3.2" fill={hi} opacity="0.85" />
      <path
        d="M28 42 h8 v6 c0 3 -2 5 -4 5 s-4 -2 -4 -5 z"
        fill={`url(#${gid})`}
        stroke={style.stroke}
        strokeWidth="1"
      />
      <path
        d="M22 54 h20 l2 4 H20 z"
        fill={`url(#${gid})`}
        stroke={style.stroke}
        strokeWidth="1"
      />
      <rect x="18" y="57.5" width="28" height="3.5" rx="1.2" fill={`url(#${gid})`} stroke={style.stroke} strokeWidth="1" />
    </svg>
  );
}

function EmptyCup({ uid = 'empty' }) {
  return (
    <svg
      width={48}
      height={48}
      viewBox="0 0 64 64"
      aria-hidden="true"
      className="shrink-0 opacity-35"
    >
      <defs>
        <linearGradient id={`cup-empty-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#D1D5DB" />
          <stop offset="100%" stopColor="#9CA3AF" />
        </linearGradient>
      </defs>
      <path
        d="M14 16 C2 18 2 34 16 36"
        fill="none"
        stroke={`url(#cup-empty-${uid})`}
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M50 16 C62 18 62 34 48 36"
        fill="none"
        stroke={`url(#cup-empty-${uid})`}
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M18 12 h28 a4 4 0 0 1 4 4 v6 c0 12 -8 20 -18 20 s-18 -8 -18 -20 v-6 a4 4 0 0 1 4 -4 z"
        fill={`url(#cup-empty-${uid})`}
        stroke="#6B7280"
        strokeWidth="1.2"
      />
      <path
        d="M28 42 h8 v6 c0 3 -2 5 -4 5 s-4 -2 -4 -5 z"
        fill={`url(#cup-empty-${uid})`}
        stroke="#6B7280"
        strokeWidth="1"
      />
      <path
        d="M22 54 h20 l2 4 H20 z"
        fill={`url(#cup-empty-${uid})`}
        stroke="#6B7280"
        strokeWidth="1"
      />
      <rect x="18" y="57.5" width="28" height="3.5" rx="1.2" fill={`url(#cup-empty-${uid})`} stroke="#6B7280" strokeWidth="1" />
    </svg>
  );
}

function yearFromTrophy(t) {
  const iso = t.starts_at || t.awarded_at || t.closed_at || t.ends_at;
  if (iso) {
    const match = String(iso).match(/^(\d{4})/);
    if (match) return Number(match[1]);
  }
  const fromName = String(t.season_name || '').match(/(19|20)\d{2}/);
  if (fromName) return Number(fromName[0]);
  return null;
}

function sortKey(t) {
  return `${t.starts_at || ''}|${t.awarded_at || t.closed_at || ''}|${t.season_id || ''}`;
}

function groupTrophiesByYear(trophies) {
  const groups = new Map();
  for (const t of trophies) {
    const year = yearFromTrophy(t);
    const key = year == null ? 'other' : String(year);
    if (!groups.has(key)) groups.set(key, { year, items: [] });
    groups.get(key).items.push(t);
  }
  for (const group of groups.values()) {
    group.items.sort((a, b) => (sortKey(a) < sortKey(b) ? 1 : -1));
  }
  return Array.from(groups.values()).sort((a, b) => {
    if (a.year == null) return 1;
    if (b.year == null) return -1;
    return b.year - a.year;
  });
}

function rankLabel(rank) {
  if (rank === 1) return '1re place';
  return `${rank}e place`;
}

function SeasonCard({ item, onOpen }) {
  const points = Number(item.points) || 0;
  const unranked = points <= 0 || item.rank == null;
  const medalStyle = item.medal ? MEDAL_STYLES[item.medal] : null;

  return (
    <li className="w-[9.5rem] sm:w-[10.5rem]">
      <button
        type="button"
        onClick={() => onOpen?.(item)}
        className="w-full flex flex-col items-center text-center rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-200/80 dark:border-gray-700/80 px-2.5 py-3 hover:border-[#f76f4b]/60 hover:bg-orange-50/40 dark:hover:bg-orange-950/20 transition-colors cursor-pointer"
      >
        {item.medal ? (
          <TrophyCup medal={item.medal} uid={item.season_id} size={44} />
        ) : (
          <EmptyCup uid={item.season_id} />
        )}
        <p className="mt-2 w-full text-xs font-semibold text-[#080808] dark:text-white leading-snug line-clamp-2">
          {item.season_name}
        </p>
        {unranked ? (
          <p className="mt-1 text-[11px] text-gray-400">non classé</p>
        ) : (
          <p className={`mt-1 text-[11px] leading-snug ${medalStyle ? medalStyle.text : 'text-gray-500 dark:text-gray-400'}`}>
            {medalStyle ? `${medalStyle.label} · ` : ''}
            {rankLabel(item.rank)}
            <br />
            {points} pts
          </p>
        )}
      </button>
    </li>
  );
}

export default function Vitrine() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [trophies, setTrophies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedYears, setCollapsedYears] = useState({});
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [rankingTab, setRankingTab] = useState('cumul');
  const [rankingFiltersHost, setRankingFiltersHost] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API}/group/games/my-trophies`, { headers: authHeaders(token) });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || 'Impossible de charger la vitrine');
        }
        const data = await res.json();
        if (!cancelled) setTrophies(data.trophies || []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Erreur');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const yearGroups = useMemo(() => groupTrophiesByYear(trophies), [trophies]);

  const toggleYear = (key) => {
    setCollapsedYears((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const openSeasonRanking = (item) => {
    setSelectedSeason(item);
    setRankingTab(item?.status === 'active' ? 'live' : 'cumul');
    setRankingFiltersHost(null);
  };

  return (
    <Layout onLogout={logout} API={API} favoriteApps={[]}>
      <div className="max-w-5xl mx-auto px-4 py-8 sm:px-6">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-[#f76f4b] mb-6"
        >
          <ArrowLeft size={16} />
          Accueil
        </button>

        <header className="mb-8">
          <h1 className="text-2xl font-bold text-[#080808] dark:text-white">Ma vitrine</h1>
        </header>

        {loading ? (
          <p className="text-sm text-gray-400">Chargement…</p>
        ) : error ? (
          <p className="text-sm text-red-500">{error}</p>
        ) : trophies.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 px-6 py-12 text-center">
            <div className="flex justify-center mb-3">
              <EmptyCup uid="none" />
            </div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Aucune saison pour le moment</p>
          </div>
        ) : (
          <div className="space-y-4">
            {yearGroups.map((group) => {
              const key = String(group.year ?? 'other');
              const collapsed = !!collapsedYears[key];
              return (
                <section
                  key={key}
                  className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/50 px-4 py-3 sm:px-5"
                >
                  <button
                    type="button"
                    onClick={() => toggleYear(key)}
                    className="w-full flex items-center justify-between gap-2 text-left"
                    aria-expanded={!collapsed}
                  >
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {group.year ?? 'Autres saisons'}
                    </h2>
                    <ChevronDown
                      size={18}
                      className={`text-gray-400 shrink-0 transition-transform ${collapsed ? '-rotate-90' : ''}`}
                    />
                  </button>
                  {!collapsed && (
                    <ul className="mt-3 flex flex-wrap-reverse justify-center gap-3">
                      {group.items.map((t) => (
                        <SeasonCard key={t.season_id} item={t} onOpen={openSeasonRanking} />
                      ))}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>

      <Dialog
        open={!!selectedSeason}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedSeason(null);
            setRankingFiltersHost(null);
          }
        }}
      >
        <DialogContent
          data-game-season-dialog=""
          overlayClassName="lg:left-64"
          className="max-h-[90vh] overflow-hidden text-left h-[90vh] flex flex-col pr-12 w-[calc(100vw-1.5rem)] max-w-[calc(100vw-1.5rem)] data-[state=open]:zoom-in-100 data-[state=open]:slide-in-from-left-0 lg:w-auto lg:max-w-none"
          style={{ zIndex: 10200 }}
        >
          {selectedSeason ? (
            <div className="min-w-0 flex-1 min-h-0 overflow-y-auto pr-3">
              <div className="flex items-start justify-between gap-3 min-w-0">
                <div className="min-w-0">
                  <DialogTitle className="text-base leading-snug break-words whitespace-normal text-left flex items-center gap-2 min-w-0">
                    <Gamepad2 size={16} className="text-[#f76f4b] shrink-0" />
                    <span className="truncate">{selectedSeason.season_name}</span>
                  </DialogTitle>
                  <p className="text-xs text-gray-400 mt-0.5">Classement de la saison</p>
                </div>
                <div className="shrink-0 flex flex-col items-end gap-1.5 max-w-[min(100%,24rem)]">
                  <FilterPills
                    value={rankingTab}
                    options={RANKING_VIEW_OPTIONS}
                    onChange={setRankingTab}
                    align="end"
                    compact
                    nowrap
                  />
                  <div ref={setRankingFiltersHost} className="flex flex-col items-end gap-1.5 w-full" />
                </div>
              </div>
              <SeasonRankingPanel
                key={selectedSeason.season_id}
                token={token}
                meId={user?.id}
                seasonId={selectedSeason.season_id}
                compact
                tab={rankingTab}
                onTabChange={setRankingTab}
                hideMainTabs
                filtersHost={rankingFiltersHost}
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
